import {
  CertificationApiS,
  CertificationConstantS,
  CertificationSchemaS,
  CertificationValidationS,
} from '@/features/Certification/outbound/share';
import type { ErrorJson } from '@/libs/errors';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import { replaceBrToSpace } from '@/libs/util/replaceBrToSpace';
import { Button, Input, InputChangeEvent, InputTimerEndEvent, Label, Typo } from '@/ui/component/atom';
import { ModalBodyView, Toast } from '@/ui/component/cell';
import { pipe } from '@fxts/core';
import { isAxiosError } from 'axios';
import { html } from 'rune-ts';
import { FindIdResultView } from '../FindIdResult/FindIdResult';
import { makeErrorPopup } from '../Popup/makeErrorPopup';
import klass from './FindId.module.scss';

export interface FindIdViewData {}

export class FindIdView extends ModalBodyView<FindIdViewData> {
  valid_code = false;
  requested_code = false;
  component: {
    mobile: Input;
    name: Input;
    code: Input;
    request_code: Button;
  };

  constructor(data: FindIdViewData) {
    super(data);

    this.component = {
      name: new Input(
        {},
        {
          attr: {
            name: 'name',
            type: 'text',
            placeholder: T('auth.find_id.name_placeholder'),
            required: true,
          },
        },
      ),
      mobile: new Input(
        {},
        {
          attr: {
            name: 'mobile',
            type: 'text',
            placeholder: T('auth.find_id.mobile_placeholder'),
            inputmode: 'numeric',
            required: true,
          },
          delete_pattern: /\D/g,
        },
      ),
      code: new Input(
        {},
        {
          attr: {
            name: 'code',
            type: 'text',
            placeholder: T('auth.find_id.code_placeholder'),
            maxlength: 6,
            required: true,
            inputmode: 'numeric',
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('auth.find_id.code_time_end'),
              },
            ],
          },
          controller: {
            type: 'timer',
            timer: {
              minutes: 5,
              autoplay: false,
            },
          },
          klass: `${klass.code} ${klass.disabled}`,
          delete_pattern: /\D/g,
        },
      ),
      request_code: new Button(
        {
          label: new Typo({ text: T('auth.find_id.request_mobile_verify_code') }, { typo: 'p2', weight: 'bold' }),
        },
        {
          size: 'small',
          disabled: true,
          type: 'button',
          klass: `${klass.button} request_code`,
        },
      ),
    };
  }

  override template() {
    return html`
      <div class="${klass.container}">
        ${new Label({ label: T('auth.find_id.name'), input: this.component.name }, { required: true })}
        <div class="${klass.container} ${klass.input_container}">
          ${new Label(
            { label: T('auth.find_id.mobile'), input: this.component.mobile, button: this.component.request_code },
            { required: true },
          )}
          ${this.component.code}
        </div>
      </div>
    `;
  }

  override async confirm() {
    await this.requestFindEmail();
    return false;
  }

  override onRender() {
    this.component.name.focus();
    this.delegate(InputChangeEvent, Input, this.inputChangeHandler);
    this.delegate(InputTimerEndEvent, Input, this.timerEndHandler);
    this.delegate('click', '.request_code', this.requestCertificationCode);
  }

  timerEndHandler() {
    const modal = this.getModal();
    modal.button.confirm.disabled();
    return;
  }

  getFindIdData() {
    const name = this.component.name.data.value;
    const mobile = this.component.mobile.data.value;
    const code = this.component.code.data.value;

    return {
      name,
      mobile,
      code,
    };
  }

  async inputChangeHandler(_e: Event, input: Input) {
    if (!this.requested_code) {
      const mobile_certification_request_data_validation =
        CertificationSchemaS.CreateMobileCertificationReqBody.safeParse({
          ...this.getFindIdData(),
          certification_type: CertificationConstantS.CERTIFICATION_TYPE.FIND_EMAIL,
        });

      if (mobile_certification_request_data_validation.success && this.component.request_code.option.disabled) {
        this.component.request_code.enabled();
        return;
      }

      if (!mobile_certification_request_data_validation.success && !this.component.request_code.option.disabled) {
        this.component.request_code.disabled();
        return;
      }

      return;
    }

    if (input.option.attr?.name === 'code' && this.requested_code && !this.valid_code) {
      await this.requestCodeVerifyHandler();
    }
  }

  async requestCertificationCode() {
    try {
      await pipe(
        { ...this.getFindIdData(), certification_type: CertificationConstantS.CERTIFICATION_TYPE.FIND_EMAIL },
        CertificationValidationS.validateCreateMobileCertificationReqBody,
        CertificationApiS.postMobileCertification,
      );

      if (!this.requested_code) {
        this.requested_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.find_id.request_mobile_verify_code_retry') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.option.color = 'secondary';
        this.component.request_code.redraw();

        this.component.code.option.klass = `${klass.code} ${klass.fade_in}`;
        this.component.code.enabled();
      }

      this.component.mobile.resetValidator();
      this.component.code.inputBaseView.setValue('');
      this.component.code.inputControllerView.restart();
      this.component.code.focus();

      this.component.name.disabled();
      this.component.mobile.disabled();

      const modal = this.getModal();
      modal.button.confirm.disabled();

      Toast.open({ label: T('common.send_sms_with_certification_code'), close_label: T('common.close') }, {});
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        this.component.mobile.changeValidatorOnce({
          message: replaceBrToSpace(e.response?.data?.message ?? T('common.errors.message')),
          validator: () => false,
        });

        if (this.requested_code) {
          this.requested_code = false;

          this.component.request_code.data.label = new Typo(
            { text: T('auth.find_id.request_mobile_verify_code') },
            { typo: 'p2', weight: 'bold' },
          );
          this.component.request_code.redraw();

          this.component.code.option.klass = `${klass.code} ${klass.fade_out}`;
          this.component.code.inputControllerView.stop();
          this.component.code.redraw();
        }

        return;
      }

      return makeErrorPopup(e);
    }
  }

  async requestCodeVerifyHandler() {
    try {
      if (this.component.code.data.value?.length === 6) {
        await pipe(
          {
            ...this.getFindIdData(),
            certification_type: CertificationConstantS.CERTIFICATION_TYPE.FIND_EMAIL,
          },
          CertificationValidationS.validateVerfityMobileCertificationReqBody,
          CertificationApiS.postVerifyMobileCertification,
        );

        this.valid_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.find_id.request_mobile_verify_code_valid') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.disabled();

        this.component.code.option.klass = `${klass.code} ${klass.fade_out}`;
        this.component.code.inputControllerView.stop();
        this.component.code.redraw();

        const modal = this.getModal();
        modal.button.confirm.enabled();
      }
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        if (e.response?.data.code === 'CERTIFICATION_004') {
          return this.component.code.changeValidatorOnce({
            message: replaceBrToSpace(e.response.data?.message ?? T('common.errors.title')),
            validator: () => false,
          });
        } else {
          return makeErrorPopup(e, e.response?.data);
        }
      }

      return makeErrorPopup(e);
    }
  }

  async requestFindEmail() {
    try {
      const res = await pipe(
        this.getFindIdData(),
        CertificationValidationS.validateFindEmailReqBody,
        CertificationApiS.postFindEmail,
      );

      const modal = this.getModal();
      modal.option.footer.type = 'confirm_only';
      modal.button.confirm.data.label = new Typo({ text: T('auth.signup.go_login') }, { weight: 'bold' });
      modal.button.confirm.enabled();
      modal.footer.redraw();
      await modal.transition(new FindIdResultView(res.data));
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError<ErrorJson>(e)) {
        if (e.response?.data.code === 'CERTIFICATION_006') {
          await makeErrorPopup(e, e.response?.data);
          return this.getModal().button.cancel.element().click();
        }

        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(e, {});
    }
  }
}
